import { Component, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SearchStateService } from '../../../shared/services/search-state.service';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { PostGetLocationDetailsRequest } from '../../../../shared/model/providerlocationsearch/PostGetLocationDetailRequest';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../../environments/environment';
import { GetCareNowFormatService } from '../../../../get-care-now-feature/utilities/getcarenow-format.service';

@Component({
  selector: 'app-location-search-results-card-mobile',
  standalone: true,
  imports: [
    CommonModule
  ],
  templateUrl: './location-search-results-card.component.html',
  styleUrls: ['./location-search-results-card.component.css']
})
export class LocationSearchResultsCardComponent {
  @Input() location: any;
  @Input() isPlus: boolean = false;
  @Input() queryCityOrZip!: any;
  locale = "en-US";

  private searchState = inject(SearchStateService);
  private mcAppGateway = inject(MCAppGatewayService);

  getDirectionsText = 'Get directions';
  milesAwayText = 'mi away';
  currentlyClosedText = 'Currently closed';
  vucBtnText = '24/7 Virtual Care'; 
  scheduleBtnText = 'Schedule'
  viewDetailsBtnText = 'View details';
  reserveSpotBtnText = "Reserve my spot";
  asSoonAs = "Book as soon as"; 
  walkInOnlyText = 'Walk-in only';
  waitTimeText = 'Wait time';
  minutesText = 'minutes';
  nextAvailabilityFormatted = '';

  screenWidth = window.innerWidth;
  private getCareNowFormatService: GetCareNowFormatService;

  constructor(private translate: TranslateService) {
    this.locale = sessionStorage.getItem("locale") || "en-US";
    this.translate.use(this.locale);
    this.getCareNowFormatService = new GetCareNowFormatService(this.translate);
  }

  ngOnInit() {
    window.addEventListener('resize', () => {
      this.screenWidth = window.innerWidth;
    });

    this.nextAvailabilityFormatted = this.getCareNowFormatService.formatNextAvailability(this.location?.nextAvailability);

    this.translate.get('GET_DIRECTIONS').subscribe((translated: string) => {
      this.getDirectionsText = translated;
    });

    this.translate.get('MILES_AWAY').subscribe((translated: string) => {
      this.milesAwayText = translated;
    });

    this.translate.get('CURRENTLY_CLOSED').subscribe((translated: string) => {
      this.currentlyClosedText = translated;
    });

    this.translate.get('247VUC').subscribe((translated: string) => {
      this.vucBtnText = translated;
    });

    this.translate.get('SCHEDULE').subscribe((translated: string) => {
      this.scheduleBtnText = translated;
    });

    this.translate.get('VIEW_DETAILS').subscribe((translated: string) => {
      this.viewDetailsBtnText = translated;
    });

    this.translate.get('RESERVE_MY_SPOT').subscribe((translated: string) => {
      this.reserveSpotBtnText = translated;
    });

    this.translate.get('BOOK_AS_SOON_AS').subscribe((translated: string) => {
      this.asSoonAs = translated;
    });

    this.translate.get('WALKIN').subscribe((translated: string) => {
      this.walkInOnlyText = translated;
    });

    this.translate.get('WAIT_TIME').subscribe((translated: string) => {
      this.waitTimeText = translated;
    });

    this.translate.get('MINUTES').subscribe((translated: string) => {
      this.minutesText = translated;
    });
  }

  viewLocationDetails(): void {
    if (!this.location?.locationId) return;
    
    // Set the current location in state
    this.searchState.setCurrentLocationDetails(this.location);
    this.searchState.setShowSpinner(true);
    
    // Make API call to get full location details
    const request = new PostGetLocationDetailsRequest();
    request.locationID = this.location.locationId;
    request.cityOrZip = this.queryCityOrZip;
    
    this.mcAppGateway.PostGetLocationDetails(request).subscribe({
      next: (response: any) => {
        console.log("Location Details: => ", response);
        this.searchState.setCurrentLocationDetails(response);
        this.searchState.setShowSpinner(false);
      },
      error: (error) => {
        console.error('Error fetching location details:', error);
        this.searchState.setShowSpinner(false);
      }
    });
  }

  getDistanceText(): string {
    if (this.location?.distanceInMiles != null) {
      return `${this.location.distanceInMiles.toFixed(1)}mi away`;
    }
    return '';
  }

  getDirections(event: Event): void {
    event.stopPropagation();
    if (this.location?.locationDirection) {
      window.location.href = this.location.locationDirection;
    }
  }

  onCallPhone(event: Event) {
    event.stopPropagation();
    if (this.location?.phone) {
      window.location.href = `tel:${this.location.phone}`;
    }
  }

  hasPhone(): boolean {
    return this.location?.phone != null && this.location.phone !== '';
  }

  hasAddress(): boolean {
    return this.location?.address1 != null && this.location.address1 !== '';
  }

  getFullAddress(): string {
    const parts = [];
    if (this.location?.address1) parts.push(this.location.address1);
    if (this.location?.address2) parts.push(this.location.address2);
    return parts.join(', ');
  }

  getCityStateZip(): string {
    const parts = [];
    if (this.location?.city) parts.push(this.location.city);
    if (this.location?.state) parts.push(this.location.state);
    if (this.location?.zip) parts.push(this.location.zip);
    return parts.filter(p => p).join(', ');
  }

  getLocationNameLines(): string[] {
    const locationName = this.location?.locationName || 'Unknown Location';
    const prefix = 'MemorialCare Medical Group - ';
    const prefix2 = 'MemorialCare Urgent Care - ';
    
    if (locationName.startsWith(prefix)) {
      const remainingText = locationName.substring(prefix.length);
      if (remainingText.length > 11) {
        return [prefix.trim(), remainingText];
      }
    }
    else if (locationName.startsWith(prefix2)) {
      const remainingText = locationName.substring(prefix2.length);
      if (remainingText.length > 11) {
        return [prefix2.trim(), remainingText];
      }
    }
    
    return [locationName];
  }

  getActionType(): 'view' | 'reserve' | 'virtual' {
    if(this.location?.departmentId != null && this.location?.deparmentId != '')  {
      
      if(this.location?.isOpen === false) {
        return 'virtual';
      }
      else if (this.location?.nextAvailability) {
        return 'reserve';
      }
    } 
    // default to view location details   
    return 'view';
  }

  getActionButtonText(): string {
    const actionType = this.getActionType();
    if (actionType === 'virtual') {
      return this.vucBtnText;
    } 
    else if (actionType === 'reserve') {
      return this.reserveSpotBtnText;
    }
    return this.viewDetailsBtnText;
  }

  getStatusText(): string {
    if (this.location?.isOpen === false) {
      return this.currentlyClosedText;
    } 
    else if (this.location?.nextAvailability) {
      return this.asSoonAs;
    } 
    else if (this.location?.waitTime) {
      return this.walkInOnlyText;
    } 

    return '';
  }

  hasStatus(): boolean {
    return this.location?.isOpen === false || this.location?.waitTime != null || this.location?.nextAvailability != null;
  }

  handleAction(event: Event): void {
    event.stopPropagation();
    
    const actionType = this.getActionType();
    if (actionType === 'virtual') {
      // Handle virtual care
      console.log('Opening virtual care...');
      this.onVirtualCareBtnClicked();
    } else if (actionType === 'reserve') {
      // Handle reservation
      console.log('Reserving spot...');
      this.onReserveMySpot();
    } else {
      // Handle view details
      this.viewLocationDetails();
    }
  }

  onVirtualCareBtnClicked() {
    const eptID = sessionStorage.getItem("eptID");
    const isMobile = window.location.href.includes('mobile');

    if (eptID == null || eptID == undefined || eptID == "") {
      this.vucNowUnAuth(isMobile);
    }
    else {
      this.vucNowAuth();
    } 
  }


  vucNowUnAuth(isMobile: boolean) {
  
      // mobile deeplink --> mymemorialcare://openscheduling/standalone?id=7000892&vt=52216,52220
      var deeplink = isMobile ? environment.mobile_open_scheduling_deep_link_base_url : environment.web_open_scheduling_deep_link_base_url; 
      
      deeplink += "/standalone?id=" + environment.generic_vuc_provider_id
        + "&vt=" + environment.adult_vuc_rfv_id;
        
      // this.monitoringService.logEvent('VUCNow/NoAuth', { feature: 'get-care-now', subfeature: 'IPUC', platform: 'web' }); // log as web event
      var searchMethod = sessionStorage.getItem('locationSearchMethod') || 'unknown';  

      if (searchMethod === 'quickSearch') {
        deeplink += "&linksource=PLUALOCQSIPUCRESERVE";
      }

      console.log("VUC Now clicked (IPUC)");
      console.log(deeplink);
      
      window.location.href = deeplink;
    
    }
  
    vucNowAuth() {
      var deepLink = '';
  
      if (this.isPlus == null) {
        deepLink = environment.web_vuc_deep_link_base_url; // For web users, use the web deep link
        // this.monitoringService.logEvent('VUCNow', { feature: 'find-care', subfeature: 'IPUC', platform: 'web' }); // log as web event
      }
      else {
        deepLink = this.isPlus == true
          ? environment.mobile_vuc_deep_link_base_url // For Plus users, use the mobile deep link
          : environment.epic_vuc_deep_link_base_url; //  For non-Plus users, use the epic deep link
  
        var mobilePlatform = this.isPlus ? 'mobile-plus' : 'mobile-standard';
        // this.monitoringService.logEvent('VUCNow', { feature: 'find-care', subfeature: 'IPUC', platform: mobilePlatform }); // log as mobile or web event
      }
  
      const age = sessionStorage.getItem("age");
      var ageNumber = age ? parseInt(age) : null;
  
      if (ageNumber != null) {
          deepLink += ageNumber < 18 
            ? environment.pediatric_vuc_prc_id
            : environment.adult_vuc_prc_id;
      }
      
      window.location.href = deepLink;
    }

  onReserveMySpot() {
    const eptID = sessionStorage.getItem("eptID");
    const isMobile = window.location.href.includes('mobile');

    if (eptID == null || eptID == undefined || eptID == "") {
      this.reserveNowUnAuth(isMobile);
    }
    else {
      this.reserveNowAuth();
    }
  }

  reserveNowUnAuth(isMobile: boolean) {
    // Logic to show more options or details for unauthenticated users
    var deepLink = isMobile ? environment.mobile_omw_deep_link_base_url : environment.web_unauth_omw_deep_link_base_url;
    deepLink += this.location.departmentId;

    // this.monitoringService.logEvent('Schedule/NoAuth', { feature: 'get-care-now', subfeature: 'IPUC', platform: isMobile ? 'mobile-plus' : 'web' }); // log as web event

    var searchMethod = sessionStorage.getItem('locationSearchMethod') || 'unknown';

    if (searchMethod === 'quickSearch') {
      deepLink += "&linksource=PLUALOCQSIPUCRESERVE";
    }

    console.log("reserve now clicked (IPUC)");
    console.log(deepLink);

    window.location.href = deepLink;
  }

  reserveNowAuth() {
    var deepLink = '';
    const isPlus = sessionStorage.getItem("isPlus") != null ? sessionStorage.getItem("isPlus") === 'true'  : null;

    if(isPlus == null) {
      deepLink = environment.mobile_omw_deep_link_base_url;
      // this.monitoringService.logEvent('Schedule', { feature: 'get-care-now', subfeature: 'IPUC', platform: 'web' }); // log as web event
    } 
    else {
      deepLink = isPlus
          ? environment.mobile_omw_deep_link_base_url // For Plus users, use the mobile deep link
          : environment.epic_omw_deep_link_base_url; //  For non-Plus users, use the epic deep link
    
      var mobilePlatform = isPlus ? 'mobile-plus' : 'mobile-standard';
      // this.monitoringService.logEvent('Schedule', { feature: 'get-care-now', subfeature: 'IPUC', platform: mobilePlatform });
    }
   
    // add provider id and department id params to the deep link
    deepLink += this.location.departmentId;

    var searchMethod = sessionStorage.getItem('locationSearchMethod') ?? "unknown";
    
    if (searchMethod === 'quickSearch') {
      deepLink += "&linksource=AUTHHMLOCQSUCRESERVE";
    }
    
    console.log("reserve now clicked (IPUC)");
    console.log(deepLink);

    window.location.href = deepLink;
  }


}
